import { getMessaging, getToken } from 'firebase/messaging'
import { UtilsService } from './utils.service'

// Métodos que só existem no browser (Notification, navigator, document, service worker, firebase/messaging).
// Instanciada apenas no frontend (api.service.ts) — NÃO deve ser copiada para o backend.
export class UtilsBrowserService extends UtilsService {
    async requestPushNotificationPermission() {
        const permission = await Notification.requestPermission()
        if (permission === 'granted') {
            window.log('[utils]NotifPerm: Notification permission is granted.')
            //const c = navigator.serviceWorker.controller;
            //c.postMessage({ 'act': 'goSub', 'pid': getParameterByName('pid', self.location) });
            const fcmToken = await this.getFcmTokenWithFirebase()
            if (fcmToken) {
                await window.api.users.users.upsertFCMToken(fcmToken)
            }
        } else if (permission === 'denied') {
            window.log('[utils]NotifPerm: Notification permission result: ' + permission, 'error', { permission })
            window.error('Permissão negada para notificações: ' + permission)
        } else {
            window.log('[utils]NotifPerm: Notification permission result: ' + permission, 'error', { permission })
            window.error('Permissão negada para notificações: ' + permission)
        }
        return permission
    }

    async getFcmTokenWithFirebase() {
        // Get registration token. Initially this makes a network call, once retrieved
        // subsequent calls to getToken will return from cache.
        if ('serviceWorker' in navigator === false) {
            return null
        }
        try {
            const messaging = getMessaging()
            const vapidKey = 'BJ8pcldf9Gx624ubGcJ3YEm-G8IsllK-4UcfNK939Oweog7D-KdnFbaU3plG2eIymrg4dvRsEUJ49YRWrGBJnAM'
            //window.log('GetFCMToken: Solicitando token para atualizar o servidor, se necessário...')
            if (!navigator.serviceWorker.controller) {
                //window.log('GetFCMToken: Abortando. Service Worker não está registrado.')
                return null
            }
            const currentToken = await getToken(messaging, {
                serviceWorkerRegistration: await navigator.serviceWorker.ready,
                vapidKey,
            })
            if (!currentToken) {
                //window.log('GetFCMToken: No registration token available. Request permission to generate one.')
                return null
            }
            // Send the token to your server and update the UI if necessary
            //window.log('GetFCMToken: ' + currentToken)
            return currentToken
        } catch (error) {
            window.error(error)
            //window.log('GetFCMToken: An error occurred while retrieving token. ', error)
            return null
        }
    }

    async showInsideNotification(title: string, body?: string, icon?: string, image?: string) {}

    async showNotification(title: string, body?: string, icon?: string, image?: string) {
        if (typeof Notification === 'undefined') {
            window.log('showNotification: Notifications are not available.')
            return
        }
        if (Notification.permission !== 'granted') {
            window.log('showNotification: Notifications not permited yet.')
            return
        }
        const notif = new Notification(title, {
            body: body,
            tag: '',
            icon: icon,
            lang: '',
            dir: 'auto',
        })
        notif.onclick = () => {
            window.log('showNotification: notification clicked')
        }
        notif.onshow = () => {
            window.log('showNotification: notification showed')
        }
        notif.onerror = () => {
            window.log('showNotification: notification error')
        }
        notif.onclose = () => {
            window.log('showNotification: notification closed')
        }
    }

    copyToClipboard(text: string) {
        const textarea = document.createElement('textarea')
        try {
            textarea.style.position = 'absolute'
            textarea.style.top = '-300px'
            textarea.value = text
            document.body.appendChild(textarea)
            textarea.select()
            document.execCommand('copy')
            document.body.removeChild(textarea)
        } catch (error) {
            try {
                document.body.removeChild(textarea)
            } catch (error) {}
            navigator.clipboard.writeText(text)
        }
    }

    isIOS() {
        return (
            ['iPad Simulator', 'iPhone Simulator', 'iPod Simulator', 'iPad', 'iPhone', 'iPod'].includes(navigator.platform) ||
            // iPad on iOS 13 detection
            (navigator.userAgent.includes('Mac') && 'ontouchend' in document)
        )
    }

    isInstalled() {
        if (window.matchMedia('(display-mode: standalone)').matches || window?.navigator['standalone']) {
            return true
        }
        return false
    }

    isWindows() {
        const isWindows = /Windows NT/.test(navigator.userAgent)
        return isWindows
    }

    isMacOS() {
        const isMacOS = /Macintosh/.test(navigator.userAgent) && !/Mobile/.test(navigator.userAgent)
        return isMacOS
    }

    isMobile() {
        if (this.isWindows()) return false
        //if (this.isMacOS()) return false // desabilitado para evitar falso positivo no ipad. como mac nunca é touch, então isTouchDevice é seguro.
        const isTouchDevice = () => window.matchMedia('(pointer: coarse)').matches && !window.matchMedia('(hover: hover)').matches
        if (isTouchDevice()) return true
        if (navigator.maxTouchPoints > 1) return true
        return window.screen.width <= 900
    }

    async toggleTheme() {
        let current = await window.flags('theme', true)
        current = !current || current === 'theme_dark' ? 'theme_light' : 'theme_dark'
        await this.setTheme(current)
    }

    loadTheme(newTheme) {
        const body = document.getElementsByTagName('body')[0]
        const themes = body.classList as any
        for (const theme of themes) {
            if (theme.startsWith('theme_')) body.classList.remove(theme)
        }
        body.classList.add(newTheme)
    }

    async setTheme(newTheme) {
        await this.loadTheme(newTheme)
        await window.flags('theme', true, newTheme)
    }

    setCookie(name, value, expirationDays) {
        const expires = new Date(Date.now() + expirationDays * 24 * 60 * 60 * 1000).toUTCString()
        document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/`
    }

    getCookie(cookieName) {
        const cookies = document.cookie.split('; ')
        for (const cookie of cookies) {
            const [name, value] = cookie.split('=')
            if (name === cookieName) {
                return decodeURIComponent(value)
            }
        }
        return null // Retorna null se o cookie não for encontrado
    }

    saveResponseAs(response, responseType, fileName: string = null) {
        const ext = responseType.split('/').length > 1 ? responseType.split('/')[1] : 'txt'
        const blob = new Blob([response], { type: responseType })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = fileName || 'arquivo.' + ext // Nome do arquivo para salvar
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
    }

    getZanduVersion() {
        return this.getScriptParam('v')
    }

    getScriptParam(param) {
        const scripts = document.getElementsByTagName('script') as unknown as any[]
        for (const script of scripts) {
            const qs = script.src.split('?')
            if (qs.length <= 1) {
                continue
            }
            const urlParams = new URLSearchParams('?' + qs[1])
            const result = urlParams.get(param)
            return result
        }
    }
}
